import { onMounted, onUnmounted, ref } from 'vue';

const CHECK_INTERVAL_MS = 5 * 60_000;
const IDLE_RELOAD_AFTER_MS = 10 * 60_000;
const IDLE_POLL_MS = 60_000;

/**
 * Keeps the eternally-open PWA window on the latest frontend release.
 *
 * Polls `/version.json` (emitted at build time, served with `no-cache` by
 * Caddy) every 5 minutes and whenever the tab becomes visible. On a version
 * mismatch it shows a sticky toast with a manual reload button; additionally it
 * silently reloads once the user has been idle for ≥ 10 min and has no unsent
 * text in any input (so a reload never discards in-progress typing).
 *
 * No-op during dev / non-release builds (`__APP_VERSION__ === 'dev'`), where
 * `version.json` is not emitted.
 */
export const useVersionCheck = () => {
  const updateAvailable = ref(false);

  const toast = useToast();

  let notified = false;
  let lastActivityAt = Date.now();
  let checkTimer: number | null = null;
  let idleTimer: number | null = null;

  const onActivity = () => {
    lastActivityAt = Date.now();
  };

  const hasDirtyInput = (): boolean => {
    const fields = document.querySelectorAll<HTMLInputElement | HTMLTextAreaElement>(
      'textarea, input:not([type]), input[type="text"], input[type="search"], input[type="email"]',
    );

    for (const field of fields) {
      if (field.value.trim().length > 0) {
        return true;
      }
    }

    const editables = document.querySelectorAll<HTMLElement>('[contenteditable="true"]');

    for (const editable of editables) {
      if ((editable.textContent ?? '').trim().length > 0) {
        return true;
      }
    }

    return false;
  };

  const check = async () => {
    try {
      const response = await fetch('/version.json', { cache: 'no-store' });

      if (!response.ok) {
        return;
      }

      const data = await response.json() as { version?: string };

      if (!data.version || data.version === __APP_VERSION__) {
        // A rolling deploy can briefly serve the new SHA then revert (a client
        // hitting different revisions); clear the flag so we never auto-reload
        // into an instance still serving the old bundle.
        updateAvailable.value = false;

        return;
      }

      updateAvailable.value = true;

      if (!notified) {
        notified = true;
        toast.add({
          title: 'Update available',
          description: 'A new version of the app is available.',
          icon: 'i-lucide-refresh-cw',
          color: 'info',
          duration: 0,
          actions: [
            {
              label: 'Reload',
              color: 'info',
              onClick: () => {
                window.location.reload();
              },
            },
          ],
        });
      }
    } catch {
      // Network hiccup — the next poll will retry.
    }
  };

  const maybeAutoReload = () => {
    if (!updateAvailable.value) {
      return;
    }

    // Only ever silently reload a backgrounded tab. A foreground tab may hold
    // unsaved non-text state (selects, toggles, drag in progress, Pinia refs) that
    // hasDirtyInput() can't see, and the app has no beforeunload guard — a
    // visible tab is only ever refreshed via the manual "Reload" toast.
    if (document.visibilityState !== 'hidden') {
      return;
    }

    if (Date.now() - lastActivityAt < IDLE_RELOAD_AFTER_MS) {
      return;
    }

    if (hasDirtyInput()) {
      return;
    }

    window.location.reload();
  };

  const onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      void check();
    }
  };

  onMounted(() => {
    if (__APP_VERSION__ === 'dev') {
      return;
    }

    window.addEventListener('pointerdown', onActivity, { passive: true });
    window.addEventListener('keydown', onActivity, { passive: true });
    document.addEventListener('visibilitychange', onVisibilityChange);

    checkTimer = window.setInterval(() => void check(), CHECK_INTERVAL_MS);
    idleTimer = window.setInterval(maybeAutoReload, IDLE_POLL_MS);

    void check();
  });

  onUnmounted(() => {
    window.removeEventListener('pointerdown', onActivity);
    window.removeEventListener('keydown', onActivity);
    document.removeEventListener('visibilitychange', onVisibilityChange);

    if (checkTimer !== null) {
      window.clearInterval(checkTimer);
    }

    if (idleTimer !== null) {
      window.clearInterval(idleTimer);
    }
  });

  return { updateAvailable };
};
